// GatePassForm.tsx - Fixed version that properly handles submission result
import React, { useState, useEffect, useCallback } from 'react';

import './GatePassForm.css';

// Import types
import { 
  GatePassWizardProps,
  Gate,
  Personnel,
  SubmissionSuccessData,
  ParentRequest
} from './types/GatePassTypes';
import { STEPS } from './constants/GatePassConstants';
import { formatLastSavedTime, validateDateRange } from './utils/GatePassUtils';
import { generateDraftName } from './draftService';

// Import custom hook
import { useGatePassForm } from './hooks/useGatePassForm';

// Import step components
import { Step1BasicInfo } from './components/steps/Step1BasicInfo';
import { Step2RoleSelection } from './components/steps/Step2GateSelection';
import { Step3ApproverReviewerSelection } from './components/steps/Step3ApproverReviewerSelection';
import { Step4ReviewSubmit } from './components/steps/Step4ReviewAndSubmit';

// Import popup components
import { GateSelectionPopup } from '../workFlow/GateSelectionPopup';
import { PersonnelSelectionPopup } from '../workFlow/PersonnelSelectionPopup';
import { ApproverSelectionPopup } from '../workFlow/ApproverSelectionPopup';
import { ReviewerSelectionPopup } from '../workFlow/ReviewerSelectionPopup';
import { SaveDraftDialog } from '../workFlow/SaveDraftDialog';
import { ParentRequestSelectionPopup } from './components/ParentRequestSelectionPopup';
import { SubmissionResult } from './components/SubmissionResult';

// Import banner components for auto-dismissing status messages
import { SuccessMessageBanner } from '../companyManagement/SuccessMessageBanner';
import { ErrorMessageBanner } from '../companyManagement/ErrorMessageBanner';

export const GatePassForm: React.FC<GatePassWizardProps> = (props) => {
  const {
    getUserInfo,
    getAccessToken,
    
    onNavigateToSavedDrafts,
    onNavigateToRequests
  } = props;

  // ═══════════════════════════════════════════════════════════════════
  // Auto-dismissing success/error banners (same pattern as CompanyManagement)
  // ═══════════════════════════════════════════════════════════════════
  const [successBanner, setSuccessBanner] = useState({ isVisible: false, message: '' });
  const [errorBanner, setErrorBanner] = useState({ isVisible: false, message: '' });

  const handleInternalStatusUpdate = useCallback((status: { type: string | null; message: string }) => {
    if (status.type === 'success') {
      setSuccessBanner({ isVisible: true, message: status.message });
    } else if (status.type === 'error') {
      setErrorBanner({ isVisible: true, message: status.message });
    } else {
      // Clear / null type — dismiss banners
      setSuccessBanner({ isVisible: false, message: '' });
      setErrorBanner({ isVisible: false, message: '' });
    }
  }, []);

  const closeSuccessBanner = useCallback(() => {
    setSuccessBanner({ isVisible: false, message: '' });
  }, []);

  const closeErrorBanner = useCallback(() => {
    setErrorBanner({ isVisible: false, message: '' });
  }, []);

  // *** FIXED: Submission state management ***
  const [submissionState, setSubmissionState] = useState<{
    isSubmitted: boolean;
    isSuccess: boolean;
    submissionData?: {
      requestId?: string;
      submissionDate?: string;
      estimatedProcessingTime?: string;
      approvalWorkflowStarted?: boolean;
    };
    errorMessage?: string;
    errorDetails?: string;
  }>({
    isSubmitted: false,
    isSuccess: false
  });

  // *** FIXED: Custom submission success handler ***
const handleSubmissionSuccess = (successData: SubmissionSuccessData) => {
  console.log('🎉 Submission successful, updating state:', successData);
  
  setSubmissionState({
    isSubmitted: true,
    isSuccess: true,
    submissionData: {
      requestId: successData.requestId,
      submissionDate: successData.submissionDate,
      estimatedProcessingTime: successData.estimatedProcessingTime,
      approvalWorkflowStarted: true
    }
  });
  
  // *** ADD THIS: Notify parent component about submission success ***
  if (props.onSubmissionStateChange) {
    props.onSubmissionStateChange(true, true);
  }
};

  // Use the custom hook with our submission success handler
  const {
    // State
    currentStep,
    formData,
    regions,
    loadingRegions,
    regionError,
    selectedGates,
    selectedPersonnel,
    personnelVisaIssues,
    approverSelection,
    reviewerSelection,
  
    isSubmitting,
    isLoadingDraft,
    draftLoadError,
    showSaveDraftDialog,
    isSavingDraft,
    lastSavedAt,
    originalRequestId,
    loadedParentRequest,
    
    // Popup states
    showGateSelection,
    showPersonnelSelection,
    showApproverSelection,
    showReviewerSelection,
    
    // Actions
    setShowGateSelection,
    setShowPersonnelSelection,
    setShowApproverSelection,
    setShowReviewerSelection,
    setSelectedGates,
    setSelectedPersonnel,
    setApproverSelection,
    setReviewerSelection,
    setShowSaveDraftDialog,
    
    // Handlers
    handleInputChange,

    handleSaveDraft,
    handleSubmit: originalHandleSubmit,
    
    // Navigation
    goToNextStep,
    goToPrevStep,
    goToStep,
    canProceedToNext,
    getStepStatus,
    
    // Utils
    fetchRegions
  } = useGatePassForm({
    ...props,
    onStatusUpdate: handleInternalStatusUpdate,
    onSubmissionSuccess: handleSubmissionSuccess // Use our custom handler
  });

  // Step validation states
  const [step2Valid, setStep2Valid] = useState(false);
  const [step3Valid, setStep3Valid] = useState(false);
  const [step4Valid, setStep4Valid] = useState(false);

  // Help guide toggle
  const [helpGuideVisible, setHelpGuideVisible] = useState(true);

  // ═══════════════════════════════════════════════════════════════════
  // UNSAVED CHANGES GUARD
  // Track whether the user has touched any form field since load/save
  // ═══════════════════════════════════════════════════════════════════
  const [isFormDirty, setIsFormDirty] = useState(false);
  const initialLoadComplete = React.useRef(false);
  const lastSavedSnapshot = React.useRef<string>('');

  // ═══════════════════════════════════════════════════════════════════
  // Return/Rejection comments — fetched from audit logs when in revision mode
  // ═══════════════════════════════════════════════════════════════════
  const [returnComments, setReturnComments] = useState<{
    comments: string;
    actionType: string;
    performedBy: string;
    performedAt: string;
  } | null>(null);
  const [loadingReturnComments, setLoadingReturnComments] = useState(false);

  // ── Supplementary request: parent request selection state ──
  const [selectedParentRequest, setSelectedParentRequest] = useState<ParentRequest | null>(null);
  const [showParentRequestPopup, setShowParentRequestPopup] = useState(false);

  // ═══════════════════════════════════════════════════════════════════
  // Restore selectedParentRequest when a draft is loaded
  // The hook exposes loadedParentRequest after loading a supplementary draft
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (loadedParentRequest) {
      setSelectedParentRequest(loadedParentRequest as ParentRequest);
      setShowParentRequestPopup(false);
      console.log('✅ selectedParentRequest restored from draft:', loadedParentRequest.id);
    }
  }, [loadedParentRequest]);

  // Auto-open parent request popup when switching to supplementary; clear when switching to new
  useEffect(() => {
    if (formData?.requestType === 'supplementary' && !selectedParentRequest && !loadedParentRequest) {
      setShowParentRequestPopup(true);
    } else if (formData?.requestType === 'new') {
      setSelectedParentRequest(null);
      setShowParentRequestPopup(false);
      // Clear parentRequestId from formData
      handleInputChange({
        target: { name: 'parentRequestId', value: '' }
      } as React.ChangeEvent<HTMLInputElement>);
    }
  }, [formData?.requestType]); // eslint-disable-line react-hooks/exhaustive-deps

  // ═══════════════════════════════════════════════════════════════════
  // Fetch return/rejection comments from audit logs when in revision mode
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!originalRequestId) {
      setReturnComments(null);
      return;
    }

    const fetchReturnComments = async () => {
      setLoadingReturnComments(true);
      try {
        const accessToken = await getAccessToken();
        const apiBase = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';
        const response = await fetch(`${apiBase}/gatepass/${originalRequestId}/audit-logs`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data && result.data.length > 0) {
            // Find the most recent RETURNED or REJECTED action with comments
            const returnOrRejectLog = result.data.find((log: any) => {
              const actionType = (log.actionType || '').toUpperCase();
              return actionType.includes('RETURN') || actionType.includes('REJECT');
            });

            if (returnOrRejectLog) {
              let comments = '';
              let performedBy = returnOrRejectLog.performedByName || returnOrRejectLog.performedByEmail || 'Approver';

              // Extract comments from the log details JSON
              if (returnOrRejectLog.details) {
                try {
                  const details = typeof returnOrRejectLog.details === 'string' 
                    ? JSON.parse(returnOrRejectLog.details) 
                    : returnOrRejectLog.details;
                  comments = details.comments || details.reason || '';
                } catch {
                  // details may not be valid JSON
                }
              }

              // Fallback: check top-level comments field
              if (!comments && returnOrRejectLog.comments) {
                comments = returnOrRejectLog.comments;
              }

              if (comments) {
                setReturnComments({
                  comments,
                  actionType: returnOrRejectLog.actionType || 'RETURNED',
                  performedBy,
                  performedAt: returnOrRejectLog.createdAt || ''
                });
                console.log('✅ Return comments loaded:', comments);
              } else {
                console.log('ℹ️ Return/reject action found but no comments attached');
                setReturnComments(null);
              }
            } else {
              console.log('ℹ️ No return/reject audit log entry found');
              setReturnComments(null);
            }
          }
        } else {
          console.warn('⚠️ Failed to fetch audit logs for return comments:', response.status);
        }
      } catch (error) {
        console.warn('⚠️ Error fetching return comments:', error);
      } finally {
        setLoadingReturnComments(false);
      }
    };

    fetchReturnComments();
  }, [originalRequestId, getAccessToken]);

  // Role selection state for Step2
  const [step2SelectedGates, setStep2SelectedGates] = useState<Gate[]>([]);
  const [step2SelectedPersonnel, setStep2SelectedPersonnel] = useState<Personnel[]>([]);

  // *** FIXED: Enhanced handleSubmit with proper error handling ***
  const handleSubmit = async () => {
  console.log('🚀 Form submission initiated...');
  
  try {
    const result = await originalHandleSubmit();
    console.log('📊 Submit result from hook:', result);
    
    if (!result.success) {
      console.error('❌ Submission failed:', result);
      setSubmissionState({
        isSubmitted: true,
        isSuccess: false,
        errorMessage: result.message || 'Submission failed',
        errorDetails: result.errorDetails
      });
      
      // *** ADD THIS: Notify parent component about submission failure ***
      if (props.onSubmissionStateChange) {
        props.onSubmissionStateChange(true, false);
      }
    }
    
  } catch (error) {
    console.error('💥 Submission error caught:', error);
    
    setSubmissionState({
      isSubmitted: true,
      isSuccess: false,
      errorMessage: error instanceof Error ? error.message : 'An unexpected error occurred',
      errorDetails: error instanceof Error ? error.stack : String(error)
    });
    
    // *** ADD THIS: Notify parent component about submission error ***
    if (props.onSubmissionStateChange) {
      props.onSubmissionStateChange(true, false);
    }
  }
};

  // *** FIXED: Action handlers for SubmissionResult ***
  const handleCreateNewRequest = () => {
    console.log('🔄 Creating new request...');
    
    // Reset submission state
    setSubmissionState({
      isSubmitted: false,
      isSuccess: false
    });

    // Reset dirty tracking
    setIsFormDirty(false);
    initialLoadComplete.current = false;

    // Reset supplementary request state
    setSelectedParentRequest(null);
    setShowParentRequestPopup(false);
    // Clear parentRequestId from formData
    handleInputChange({
      target: { name: 'parentRequestId', value: '' }
    } as React.ChangeEvent<HTMLInputElement>);

    // Clear step2 local state so new request starts clean
    setStep2SelectedGates([]);
    setStep2SelectedPersonnel([]);

     if (props.onSubmissionStateChange) {
    props.onSubmissionStateChange(false);
  }
    // Reset form to step 1
    goToStep(1);
    
    // Clear status messages
    handleInternalStatusUpdate({ type: null, message: '' });
    
    console.log('✅ Form reset for new request');
  };

  const handleViewRequests = () => {
    console.log('📋 Navigating to view requests...');
    if (onNavigateToRequests) {
      onNavigateToRequests();
    }
  };

  const handleRetrySubmission = () => {
    console.log('🔁 Retrying submission...');
    
    // Reset submission state but keep form data
    setSubmissionState({
      isSubmitted: false,
      isSuccess: false
    });

     if (props.onSubmissionStateChange) {
    props.onSubmissionStateChange(false);
  }
    
    // Go back to review step
    goToStep(4);
    
    // Clear error messages
    handleInternalStatusUpdate({ type: null, message: '' });
    
    console.log('✅ Ready for retry submission');
  };

  // ═══════════════════════════════════════════════════════════════════
  // SYNC step2 local state FROM the hook's selectedGates/selectedPersonnel.
  // 
  // When a draft is loaded, the hook sets selectedGates and selectedPersonnel.
  // We must push those into step2's local state so they render in Step2.
  // This replaces the old useEffect that was CLEARING them on every formData change.
  // ═══════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (selectedGates && selectedGates.length > 0) {
      setStep2SelectedGates(selectedGates);
    }
  }, [selectedGates]);

  useEffect(() => {
    if (selectedPersonnel && selectedPersonnel.length > 0) {
      setStep2SelectedPersonnel(selectedPersonnel);
    }
  }, [selectedPersonnel]);

  // ═══════════════════════════════════════════════════════════════════
  // REGION CHANGE — clear region-dependent selections (gates & reviewers)
  // Gates are filtered by region, and reviewers are region-scoped,
  // so changing the region invalidates any previous selections.
  // ═══════════════════════════════════════════════════════════════════
  const prevRegionIdRef = React.useRef<number | string | null | undefined>(formData?.regionId);

  useEffect(() => {
    const currentRegionId = formData?.regionId;
    const prevRegionId = prevRegionIdRef.current;

    // Update ref to current value
    prevRegionIdRef.current = currentRegionId;

    // Only clear if region actually changed (not on initial mount / draft load)
    if (prevRegionId !== undefined && prevRegionId !== null && prevRegionId !== currentRegionId) {
      console.log(`🔄 Region changed from ${prevRegionId} to ${currentRegionId} — clearing gates and reviewers`);

      // Clear selected gates (region-dependent)
      setSelectedGates([]);
      setStep2SelectedGates([]);

      // Clear selected reviewers (region-dependent)
      setReviewerSelection(null);

      // Notify user
      handleInternalStatusUpdate({
        type: 'success',
        message: 'Region changed — previously selected gates and reviewers have been cleared.'
      });
    }
  }, [formData?.regionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ═══════════════════════════════════════════════════════════════════
  // DIRTY STATE TRACKING — detect unsaved changes
  // ═══════════════════════════════════════════════════════════════════

  // Take a lightweight snapshot of all form state for comparison
  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      formData,
      gates: selectedGates.map(g => g.gateId),
      personnel: selectedPersonnel.map(p => p.personnelId ?? p.firstName),
      approvers: approverSelection?.selectedApprovers?.map(a => a.userId) ?? [],
      reviewers: reviewerSelection?.selectedReviewers?.map(r => r.userId) ?? []
    });
  }, [formData, selectedGates, selectedPersonnel, approverSelection, reviewerSelection]);

  // Mark initial load complete once draft loading is done (or immediately for new forms)
  useEffect(() => {
    if (!isLoadingDraft && !initialLoadComplete.current) {
      // Small delay so draft data settles into state before we snapshot
      const timer = setTimeout(() => {
        lastSavedSnapshot.current = getFormSnapshot();
        initialLoadComplete.current = true;
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isLoadingDraft, getFormSnapshot]);

  // Detect dirty state by comparing current form to the last saved snapshot
  useEffect(() => {
    if (!initialLoadComplete.current) return;
    const current = getFormSnapshot();
    const dirty = current !== lastSavedSnapshot.current;
    if (dirty !== isFormDirty) {
      setIsFormDirty(dirty);
    }
  }, [getFormSnapshot, isFormDirty]);

  // Notify parent component when dirty state changes
  useEffect(() => {
    if (props.onDirtyStateChange) {
      props.onDirtyStateChange(isFormDirty);
    }
  }, [isFormDirty]); // eslint-disable-line react-hooks/exhaustive-deps

  // Browser beforeunload guard — prevents tab close / refresh with unsaved data
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isFormDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isFormDirty]);

  // Reset dirty snapshot when a draft is saved (lastSavedAt updates)
  useEffect(() => {
    if (lastSavedAt && initialLoadComplete.current) {
      lastSavedSnapshot.current = getFormSnapshot();
      setIsFormDirty(false);
    }
  }, [lastSavedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Register save-draft trigger so the parent (App.tsx) can open the SaveDraftDialog
  useEffect(() => {
    if (props.onRegisterSaveDraft) {
      props.onRegisterSaveDraft(() => setShowSaveDraftDialog(true));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  

  // Handle step2 selection changes
  const handleStep2GatesChange = (gates: Gate[]) => {
    setStep2SelectedGates(gates);
    setSelectedGates(gates);
  };

  const handleStep2PersonnelChange = (personnel: Personnel[]) => {
    setStep2SelectedPersonnel(personnel);
    setSelectedPersonnel(personnel);
  };

  // Enhanced validation check
  const canProceedFromCurrentStep = () => {
    console.group(`🔍 STEP ${currentStep} VALIDATION CHECK`);
    
    let canProceed = false;
    
    switch (currentStep) {
      case 1:
        const hasValidDates = Boolean(
          formData.startDate && 
          formData.endDate && 
          validateDateRange(formData.startDate, formData.endDate)
        );
        canProceed = Boolean(
          formData.name && 
          formData.email && 
          formData.regionId && 
          formData.requestType && 
          formData.passType &&
          formData.contractNumber?.trim() &&
          hasValidDates &&
          // For supplementary requests, a parent request must be selected
          (formData.requestType !== 'supplementary' || selectedParentRequest)
        );
        console.log('Step 1 validation:', {
          name: !!formData.name,
          email: !!formData.email,
          regionId: !!formData.regionId,
          requestType: !!formData.requestType,
          passType: !!formData.passType,
          contractNumber: !!formData.contractNumber?.trim(),
          startDate: !!formData.startDate,
          endDate: !!formData.endDate,
          validDates: hasValidDates,
          parentRequestSelected: formData.requestType !== 'supplementary' || !!selectedParentRequest,
          result: canProceed
        });
        break;
        
      case 2:
        canProceed = step2Valid && personnelVisaIssues.length === 0;
        console.log('Step 2 validation:', { step2Valid, personnelVisaIssues: personnelVisaIssues.length, result: canProceed });
        break;
        
      case 3:
        canProceed = step3Valid;
        console.log('Step 3 validation:', { step3Valid, result: canProceed });
        break;
        
      case 4:
        canProceed = step4Valid;
        console.log('Step 4 validation:', { step4Valid, result: canProceed });
        break;
        
      default:
        canProceed = canProceedToNext();
        console.log('Default validation:', { result: canProceed });
        break;
    }
    
    console.log(`RESULT: Can proceed from step ${currentStep}:`, canProceed);
    console.groupEnd();
    
    return canProceed;
  };

  // Debug submission state changes
  useEffect(() => {
    console.log('📊 Submission state changed:', submissionState);
  }, [submissionState]);

  // Handle draft load error
  if (draftLoadError) {
    return (
      <div className="gate-pass-form" style={{
  minHeight: '100vh',
  padding: '0px'
}}>
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #fecaca',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: '500px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h3 style={{ margin: '0 0 12px 0', color: '#dc2626' }}>Failed to Load Draft</h3>
          <p style={{ margin: '0 0 20px 0', color: '#64748b' }}>{draftLoadError}</p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button
              onClick={() => window.location.reload()}
              className="confirm-btn"
            >
              Retry
            </button>
            {onNavigateToSavedDrafts && (
              <button
                onClick={onNavigateToSavedDrafts}
                className="cancel-btn"
              >
                Back to Drafts
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while initializing
  if (isLoadingDraft) {
    return (
      <div className="gate-pass-form" style={{
        backgroundColor: '#ffffff',
        minHeight: '100vh',
        padding: '0px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div className="loading-state">
          <div className="spinner" style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p>Loading draft...</p>
        </div>
      </div>
    );
  }

  // *** FIXED: Show SubmissionResult when submission is complete ***
  if (submissionState.isSubmitted) {
    console.log('Rendering SubmissionResult with state:', submissionState);
    
    return (
      <div className="gate-pass-form" style={{
        backgroundColor: '#ffffff',
        minHeight: '100vh',
        padding: '0px'
      }}>
        <SubmissionResult
          isSuccess={submissionState.isSuccess}
          submissionData={submissionState.submissionData}
          errorMessage={submissionState.errorMessage}
          errorDetails={submissionState.errorDetails}
          onCreateNewRequest={handleCreateNewRequest}
          onViewRequests={onNavigateToRequests ? handleViewRequests : undefined}
          onRetrySubmission={!submissionState.isSuccess ? handleRetrySubmission : undefined}
        />
      </div>
    );
  }

  // Help content per step
  const getStepHelp = (step: number) => {
    switch (step) {
      case 1:
        return {
          title: 'What is a Gate Pass?',
          content: (
            <>
              <p className="help-text">
                A gate pass authorises personnel and vehicles to access controlled areas within the facility.
              </p>
              <p className="help-subtitle">Required information</p>
              <ul className="help-list">
                <li>Select the region where access is needed</li>
                <li>Choose New or Supplementary request type</li>
                <li>Provide contract and pass validity dates</li>
              </ul>
              <p className="help-subtitle">Tips</p>
              <p className="help-text">
                Supplementary requests extend an existing approved pass. For new access, select "New" as the request type.
              </p>
            </>
          )
        };
      case 2:
        return {
          title: 'Gates & Personnel',
          content: (
            <>
              <p className="help-text">
                Select the specific gate access points required and add personnel who will use this pass.
              </p>
              <p className="help-subtitle">Important</p>
              <ul className="help-list">
                <li>Gates must be in the same region selected in Step 1</li>
                <li>All personnel must have valid visa documentation</li>
                <li>Expired visas will block submission</li>
              </ul>
            </>
          )
        };
      case 3:
        return {
          title: 'Approval Workflow',
          content: (
            <>
              <p className="help-text">
                Select the approvers and reviewers responsible for this request. You may also attach supporting documents.
              </p>
              <p className="help-subtitle">Who approves?</p>
              <p className="help-text">
                Approvers are assigned per function. Reviewers are optional and provide an additional layer of oversight before final approval.
              </p>
            </>
          )
        };
      case 4:
        return {
          title: 'Review & Submit',
          content: (
            <>
              <p className="help-text">
                Review all the details carefully before submitting. Once submitted, the approval workflow will begin automatically.
              </p>
              <p className="help-subtitle">What happens next?</p>
              <ul className="help-list">
                <li>Your company approver will be notified first</li>
                <li>Then reviewers from the contracting company (if assigned) will review the request</li>
                <li>You will be notified of any status changes</li>
              </ul>
            </>
          )
        };
      default:
        return { title: 'Help', content: <p className="help-text">Select a step to see guidance.</p> };
    }
  };

  // Render left sidebar — steps only
  const renderSidebarWizard = () => (
    <div className="wizard-sidebar">
      <div className="wizard-sidebar-steps">
        {STEPS.map((step) => {
          const status = getStepStatus(step.id);
          const isCompleted = status === 'completed';
          const isCurrent = status === 'current';

          return (
            <div
              key={step.id}
              className={`wizard-sidebar-item-wrapper ${isCurrent ? 'current' : ''} ${isCompleted ? 'completed' : ''}`}
            >
              <div className="wizard-step-bar" />
              <div
                className="wizard-sidebar-step"
                onClick={() => goToStep(step.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && goToStep(step.id)}
              >
                <div className="wizard-step-text">
                  <span className="wizard-step-title">{step.title}</span>
                  <span className="wizard-step-desc">step {step.id}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Render right help panel
  const renderHelpPanel = () => {
    const helpInfo = getStepHelp(currentStep);
    return (
      <div className={`wizard-help-right ${helpGuideVisible ? '' : 'collapsed'}`}>
        {/* Toggle tab — vertical text on the edge */}
        <button
          type="button"
          className="help-guide-toggle"
          onClick={() => setHelpGuideVisible(!helpGuideVisible)}
          aria-label={helpGuideVisible ? 'Hide Guide' : 'Show Guide'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10" />
            {helpGuideVisible ? (
              <path d="M15 9l-6 3 6 3" />
            ) : (
              <path d="M9 9l6 3-6 3" />
            )}
          </svg>
          <span>{helpGuideVisible ? 'Hide Guide' : 'Show Guide'}</span>
        </button>

        {/* Help content — visible when expanded */}
        {helpGuideVisible && (
          <div className="help-guide-content">
            <div className="help-panel-header">
              <div className="help-panel-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
            </div>
            <p className="help-title">{helpInfo.title}</p>
            {helpInfo.content}
          </div>
        )}
      </div>
    );
  };

  // Render current step content
  const renderStepContent = () => {
    if (!formData) {
      return (
        <div className="step-content">
          <p style={{ color: '#ef4444' }}>
            Form data is not available. Please try reloading the page.
          </p>
        </div>
      );
    }

    switch (currentStep) {
      case 1:
        return (
          <Step1BasicInfo 
            formData={formData}
            regions={regions}
            loadingRegions={loadingRegions}
            regionError={regionError}
            onChange={handleInputChange}
            onRefreshRegions={fetchRegions}
            selectedParentRequest={selectedParentRequest}
            onOpenParentRequestPopup={() => setShowParentRequestPopup(true)}
            onClearParentRequest={() => {
              setSelectedParentRequest(null);
              // Clear parentRequestId from formData
              handleInputChange({
                target: { name: 'parentRequestId', value: '' }
              } as React.ChangeEvent<HTMLInputElement>);
            }}
          />
        );
      
      case 2:
        return (
          <>
            <Step2RoleSelection 
              formData={{
                gateId: formData.gateId || '',
                visitorId: formData.visitorId || '',
                approverId: formData.approverId || '',
                reviewerId: formData.reviewerId || ''
              }}
              selectedGates={step2SelectedGates}
              selectedPersonnel={step2SelectedPersonnel}
              personnelVisaIssueIds={personnelVisaIssues.map(p => p.personnelId)}
              regionId={formData.regionId}
              getAccessToken={getAccessToken}
              onStatusUpdate={handleInternalStatusUpdate}
              onGatesChange={handleStep2GatesChange}
              onPersonnelChange={handleStep2PersonnelChange}
              onChange={handleInputChange}
              onValidationChange={setStep2Valid} 
            />
          </>
        );
      
      case 3:
        return (
          <Step3ApproverReviewerSelection 
            formData={{
              gateId: formData.gateId || '',
              visitorId: formData.visitorId || '',
              approverId: formData.approverId || '',
              reviewerId: formData.reviewerId || '',
              consignmentDetails: formData.consignmentDetails || '',
              detailedDescription: formData.detailedDescription || ''
            }}
            approverSelection={approverSelection}
            reviewerSelection={reviewerSelection}
            regionId={formData.regionId}
            userCompanyId={getUserInfo().companyId}
            getAccessToken={getAccessToken}
            onStatusUpdate={handleInternalStatusUpdate}
            onApproverChange={setApproverSelection}
            onReviewerChange={setReviewerSelection}
            onChange={handleInputChange}
            onValidationChange={setStep3Valid}
          />
        );
      
      case 4:
        return (
          <Step4ReviewSubmit 
            formData={formData}
            selectedGates={selectedGates}
            selectedPersonnel={selectedPersonnel}
            approverSelection={approverSelection}
            reviewerSelection={reviewerSelection}
            regions={regions}
            onValidationChange={setStep4Valid}
          />
        );
      
      default:
        return (
          <div className="step-content">
            <p style={{ color: '#ef4444' }}>
              Invalid step. Please navigate back to a valid step.
            </p>
          </div>
        );
    }
  };

  // Render navigation buttons
  const renderNavigation = () => (
    <div className="form-navigation">
      <div className="nav-left">
        <button 
          type="button"
          onClick={() => setShowSaveDraftDialog(true)}
          disabled={isSavingDraft || !formData}
          className="save-draft-btn"
        >
          {isSavingDraft ? 'Saving...' : 'Save Draft'}
        </button>

        {onNavigateToSavedDrafts && (
          <button 
            type="button"
            onClick={onNavigateToSavedDrafts}
            className="view-drafts-btn"
          >
            View Drafts
          </button>
        )}

        {lastSavedAt && (
          <span className="last-saved">
            {formatLastSavedTime(lastSavedAt)}
          </span>
        )}
      </div>

      <div className="nav-right">
        {currentStep > 1 && (
          <button 
            type="button"
            onClick={goToPrevStep}
            className="prev-btn"
          >
            Previous
          </button>
        )}

        {currentStep < STEPS.length ? (
          <button 
            type="button"
            onClick={goToNextStep}
            disabled={!canProceedFromCurrentStep()}
            className={`next-btn ${canProceedFromCurrentStep() ? 'enabled' : 'disabled'}`}
          >
            Next
          </button>
        ) : (
          <button 
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !canProceedFromCurrentStep()}
            className="submit-btn"
          >
            {isSubmitting ? 'Submitting...' : (originalRequestId ? `Resubmit Request #${originalRequestId}` : 'Submit Application')}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Auto-dismissing status banners */}
      <SuccessMessageBanner
        message={successBanner.message}
        isVisible={successBanner.isVisible}
        onClose={closeSuccessBanner}
        autoCloseDelay={5000}
      />
      <ErrorMessageBanner
        message={errorBanner.message}
        isVisible={errorBanner.isVisible}
        onClose={closeErrorBanner}
        autoCloseDelay={7000}
      />

    <div className="gate-pass-form" style={{
      backgroundColor: '#ffffff',
      minHeight: '100vh',
      padding: '0px'
    }}>
      <div className="wizard-layout">
        {/* Left: Steps */}
        {renderSidebarWizard()}

        {/* Center: Form Content */}
        <div className="wizard-content">
          {/* Page Title — inside content column like reference design */}
          <h1 style={{
            fontSize: '26px',
            fontWeight: 700,
            color: '#1a1a1a',
            margin: '0 0 24px 0',
            padding: 0,
            lineHeight: 1.3,
            fontFamily: "'Segoe UI', sans-serif",
            letterSpacing: '-0.01em',
          }}>
            {originalRequestId ? 'Edit Draft' : 'Create New Gate Pass Request'}
          </h1>

          {/* Revision Banner */}
          {originalRequestId && (
            <div style={{
              margin: '0 0 16px 0',
              backgroundColor: '#FEF3C7',
              border: '1px solid #F59E0B',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '12px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '14px',
                color: '#92400E'
              }}>
                <span style={{ fontSize: '18px' }}>🔄</span>
                <span>
                  <strong>Revision Mode:</strong> You are editing Request <strong>#{originalRequestId}</strong> that was returned for revision.
                  Upon submission, the original request will be updated (not duplicated).
                </span>
              </div>

              {/* Approver's Return/Rejection Comments */}
              {loadingReturnComments && (
                <div style={{
                  padding: '10px 20px 12px',
                  borderTop: '1px solid rgba(245, 158, 11, 0.3)',
                  fontSize: '13px',
                  color: '#92400E',
                  fontStyle: 'italic'
                }}>
                  Loading approver comments...
                </div>
              )}
              {returnComments && (
                <div style={{
                  padding: '0 20px 14px',
                  borderTop: '1px solid rgba(245, 158, 11, 0.3)',
                }}>
                  <div style={{
                    marginTop: '12px',
                    padding: '12px 16px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderLeft: '3px solid #dc2626',
                    borderRadius: '6px',
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '6px'
                    }}>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#991b1b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.03em'
                      }}>
                        {returnComments.actionType.toUpperCase().includes('REJECT') ? '❌ Rejection' : '↩️ Return'} Comments
                      </span>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>
                        {returnComments.performedBy}
                        {returnComments.performedAt && (
                          <> · {new Date(returnComments.performedAt).toLocaleDateString('en-GB', {
                            day: '2-digit', month: 'short', year: 'numeric'
                          })}</>
                        )}
                      </span>
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#1f2937',
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap'
                    }}>
                      {returnComments.comments}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step Content */}
          {renderStepContent()}

          {/* Navigation */}
          {renderNavigation()}
        </div>

        {/* Right: Help Guide */}
        {renderHelpPanel()}
      </div>

      {/* Popups */}
      <GateSelectionPopup
        isOpen={showGateSelection}
        selectedGates={selectedGates.map(gate => ({
          ...gate,
          isActive: gate.isActive ?? true,
          approvers: [],
          reviewers: [],
          approverRole: undefined,
          reviewerRole: undefined,
          description: undefined,
          createdAt: undefined,
          updatedAt: undefined
        }))}
        selectedRegionId={formData?.regionId}
        getAccessToken={props.getAccessToken}
        onClose={() => setShowGateSelection(false)}
        onConfirm={(gates) => {
          const mappedGates = gates.map(gate => ({
            gateId: gate.gateId,
            gateNumber: gate.gateNumber,
            locationName: gate.locationName,
            locationId: gate.regionId,
            regionId: gate.regionId,
            isActive: gate.isActive
          }));
          setSelectedGates(mappedGates);
          setStep2SelectedGates(mappedGates);  // Keep step2 local state in sync
          handleInternalStatusUpdate({ type: 'success', message: `${gates.length} gates selected` });
        }}
        onStatusUpdate={handleInternalStatusUpdate}
      />

      <PersonnelSelectionPopup
        isOpen={showPersonnelSelection}
        selectedPersonnel={selectedPersonnel}
        getAccessToken={props.getAccessToken}
        onClose={() => setShowPersonnelSelection(false)}
        onConfirm={(personnel) => {
          setSelectedPersonnel(personnel);
          setStep2SelectedPersonnel(personnel);  // Keep step2 local state in sync
          handleInternalStatusUpdate({ type: 'success', message: `${personnel.length} personnel selected` });
        }}
        onStatusUpdate={handleInternalStatusUpdate}
      />

      <ApproverSelectionPopup
        isOpen={showApproverSelection}
        currentSelection={approverSelection}
        userCompanyId={getUserInfo().companyId}
        getAccessToken={props.getAccessToken}
        onClose={() => setShowApproverSelection(false)}
        onConfirm={(selection) => {
          setApproverSelection(selection);
          setShowApproverSelection(false);
          const approverNames = selection.selectedApprovers.map(a => a.userName).join(', ');
          const functionName = selection.function?.functionName || 'Unknown';
          handleInternalStatusUpdate({
            type: 'success',
            message: `Approvers Selected: ${functionName} | (${selection.selectedApprovers.length}): ${approverNames}`
          });
        }}
        onStatusUpdate={handleInternalStatusUpdate}
      />

      <ReviewerSelectionPopup
        isOpen={showReviewerSelection}
        currentSelection={reviewerSelection}
        selectedRegionId={formData?.regionId}
        getAccessToken={props.getAccessToken}
        onClose={() => setShowReviewerSelection(false)}
        onConfirm={(selection) => {
          setReviewerSelection(selection);
          setShowReviewerSelection(false);
          
          if (selection.selectedReviewers && selection.selectedReviewers.length > 0) {
            const reviewerNames = selection.selectedReviewers.map(r => r.userName).join(', ');
            const functionName = selection.function?.functionName || 'Unknown';
            const companyName = selection.company?.companyName || 'Unknown';
            handleInternalStatusUpdate({
              type: 'success',
              message: `Reviewers Selected: ${companyName} - ${functionName} | (${selection.selectedReviewers.length}): ${reviewerNames}`
            });
          } else {
            handleInternalStatusUpdate({
              type: 'success',
              message: 'No Reviewers Selected - Request will proceed without review step'
            });
          }
        }}
        onStatusUpdate={handleInternalStatusUpdate}
      />

      {/* Parent Request Selection Popup (for supplementary requests) */}
      <ParentRequestSelectionPopup
        isOpen={showParentRequestPopup}
        getAccessToken={props.getAccessToken}
        onClose={() => setShowParentRequestPopup(false)}
        onSelect={(request) => {
          setSelectedParentRequest(request);
          setShowParentRequestPopup(false);
          // Sync parentRequestId into formData so it's included in submission
          handleInputChange({
            target: { name: 'parentRequestId', value: String(request.id) }
          } as React.ChangeEvent<HTMLInputElement>);
        }}
        onStatusUpdate={handleInternalStatusUpdate}
        currentSelection={selectedParentRequest}
      />

      <SaveDraftDialog
        isOpen={showSaveDraftDialog}
        suggestedName={formData ? generateDraftName(formData) : 'New Draft'}
        onClose={() => setShowSaveDraftDialog(false)}
        onSave={handleSaveDraft}
        isLoading={isSavingDraft}
      />
    </div>
    </>
  );
};